'use client';

import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { QaStatus, QrReviewBatch } from '../types';

interface QrReviewCompactRowProps {
  batch: QrReviewBatch;
}

const STATUS_BADGE_CLASSES: Record<QaStatus, string> = {
  in_qa: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  pending_qa:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  qa_passed:
    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  qa_failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
};

const STATUS_LABEL_KEYS: Record<QaStatus, string> = {
  in_qa: 'statusInQa',
  pending_qa: 'statusPendingQa',
  qa_passed: 'statusQaPassed',
  qa_failed: 'statusQaFailed'
};

export function QrReviewCompactRow({ batch }: QrReviewCompactRowProps) {
  const t = useTranslations('qrReview');
  const router = useRouter();

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      {/* Left: batch_number + container_id */}
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='truncate font-mono font-medium'>
            {batch.batch_number}
          </span>
          <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
            {batch.container_id || '—'}
          </span>
        </div>
      </div>

      {/* Center: reviewed / total (hidden on mobile) */}
      <span className='text-muted-foreground hidden text-sm tabular-nums md:inline'>
        {batch.reviewed_pages}/{batch.total_qa_pages} {t('pageSuffix')}
      </span>

      {/* Right: qa_status badge */}
      <Badge
        variant='outline'
        className={cn(
          'shrink-0 border-0 text-xs font-medium',
          STATUS_BADGE_CLASSES[batch.qa_status] ??
            'bg-muted text-muted-foreground'
        )}
      >
        {t(STATUS_LABEL_KEYS[batch.qa_status] ?? batch.qa_status)}
      </Badge>

      {/* Action button */}
      <div className='flex shrink-0 items-center'>
        <Button
          size='sm'
          variant='outline'
          onClick={() => router.push(`/dashboard/qr-review/${batch.id}`)}
        >
          {t('openForReview')}
        </Button>
      </div>
    </div>
  );
}
